import React from 'react'
import moment from 'moment'
import { DATE_TIME_FORMAT } from 'constants/app'

const SHORT_WINDOW_MESSAGE_FR = "Fenêtre de moins de 1h possible. Contacter l'APL"
const SHORT_WINDOW_MESSAGE_EN = 'Departure window shorter than 1h, Contact LPA'

/**
 * Ship preparation time - must be subtracted from descending tide end times.
 * Ships need 1 hour to prepare before they can actually leave the dock.
 */
const SHIP_PREPARATION_HOURS = 1

/**
 * Adjust end time by subtracting ship preparation time (1 hour).
 * This is applied to descending tide end times because the ship needs
 * time to prepare before the tide window closes.
 *
 * @param {string} endTime - The original end time
 * @returns {string|null} - The adjusted end time, or null if invalid
 */
const adjustEndTimeForPreparation = endTime => {
    if (!endTime) return null
    const end = moment(endTime)
    if (!end.isValid()) return null
    return end.subtract(SHIP_PREPARATION_HOURS, 'hours').toISOString()
}

/**
 * Check if a departure window is too short (less than 1 hour) AFTER adjustment.
 * The window duration is calculated from start to adjusted end time.
 */
const isWindowTooShort = (start, end) => {
    if (!start || !end) return false
    const startTime = moment(start)
    const endTime = moment(end)
    if (!startTime.isValid() || !endTime.isValid()) return false
    return endTime.diff(startTime, 'minutes') < 60
}

/**
 * Render a single time range (From/To)
 *
 * @param {string} start - Start time
 * @param {string} end - End time
 * @param {boolean} adjustEnd - If true, subtract 1h from end time (for descending tide)
 *
 * Per customer spec (2026-01-15):
 * - DESCENDING tide (adjustEnd=true): 1h mobilization applies, show warning if original window < 1h
 * - RISING tide (adjustEnd=false): NO mobilization, NO warning even for short windows (e.g., 15:00-15:45)
 *
 * The "<1h warning" is based on the ORIGINAL window duration (before 1h subtraction).
 */
const TimeRange = ({ start, end, adjustEnd = false }) => {
    const hasValidStart = start && moment(start).isValid()
    const hasValidEnd = end && moment(end).isValid()

    // Apply 1h preparation time adjustment to end time if requested
    const displayEnd = adjustEnd && hasValidEnd ? adjustEndTimeForPreparation(end) : end
    const hasValidDisplayEnd = displayEnd && moment(displayEnd).isValid()

    // Check if window is too short - ONLY for descending tide (adjustEnd=true)
    // Rising tide has no mobilization time, so no warning applies
    const isTooShort = adjustEnd && hasValidStart && hasValidEnd && isWindowTooShort(start, end)

    if (isTooShort) {
        return (
            <>
                <span>Du / From :</span> <i>- {SHORT_WINDOW_MESSAGE_FR}</i>
                <br />
                <span>Au / To :</span> <i>- {SHORT_WINDOW_MESSAGE_EN}</i>
            </>
        )
    }

    return (
        <>
            <span>Du / From :</span>{' '}
            <b>{hasValidStart ? moment(start).format(DATE_TIME_FORMAT) : '-'}</b>
            <br />
            <span>Au / To :</span>{' '}
            <b>{hasValidDisplayEnd ? moment(displayEnd).format(DATE_TIME_FORMAT) : '-'}</b>
        </>
    )
}

/**
 * Check if a dual range option has any valid data
 * A range portion is valid if it has both start AND end as valid dates
 */
const hasDualRangeData = option => {
    if (!option) return false
    const hasRising =
        option.rising &&
        option.rising.start &&
        moment(option.rising.start).isValid() &&
        option.rising.end &&
        moment(option.rising.end).isValid()
    const hasDescending =
        option.descending &&
        option.descending.start &&
        moment(option.descending.start).isValid() &&
        option.descending.end &&
        moment(option.descending.end).isValid()
    return hasRising || hasDescending
}

/**
 * Check if a standard option has valid data (both start AND end required)
 */
const hasStandardData = option => {
    if (!option) return false
    const hasValidStart = option.start && moment(option.start).isValid()
    const hasValidEnd = option.end && moment(option.end).isValid()
    return hasValidStart && hasValidEnd
}

/**
 * Render dual ranges for Scenario 4 (rising + descending with "Ou / Or")
 *
 * Note: The 1h preparation time adjustment is applied only to the descending
 * portion's end time, since that's when the tide window closes.
 */
const DualTimeRange = ({ rising, descending }) => {
    const hasRising = rising && (rising.start || rising.end)
    const hasDescending = descending && (descending.start || descending.end)

    // This should not be called if no data, but guard just in case
    if (!hasRising && !hasDescending) {
        return null
    }

    if (!hasRising) {
        // Only descending - apply 1h adjustment
        return <TimeRange start={descending.start} end={descending.end} adjustEnd={true} />
    }

    if (!hasDescending) {
        // Only rising - no adjustment needed (rising ends at peak)
        return <TimeRange start={rising.start} end={rising.end} adjustEnd={false} />
    }

    return (
        <>
            {/* Rising portion - no adjustment (ends at peak) */}
            <TimeRange start={rising.start} end={rising.end} adjustEnd={false} />
            <br />
            <b style={{ display: 'block', margin: '4px 0' }}>Ou / Or</b>
            {/* Descending portion - apply 1h adjustment */}
            <TimeRange start={descending.start} end={descending.end} adjustEnd={true} />
        </>
    )
}

/**
 * Departure options section showing 4 options based on high tides
 *
 * For Scenario 1 (latest_time): Shows single max departure time per option
 * For Scenarios 2, 3 (departure_range variants): Shows From/To range per option
 * For Scenario 4 (departure_range_pilot_updown): Shows dual ranges with "Ou / Or"
 *
 * When showExtraTimesOnly=true (for Avec pilote sections in Scenarios 3 & 4):
 * - Scenario 3: Shows only descending extra time (C-D)
 * - Scenario 4: Shows both rising and descending extra times (A-B Ou/Or C-D)
 */
export const DepartureOptions = ({
    calculationMethod,
    departureOptions = [],
    dualRangeOptions = [],
    extraTimeOptions = [],
    showExtraTimesOnly = false,
}) => {
    // Use simple string comparisons (avoid import issues)
    const isLatestTime = calculationMethod === 'latest_time'
    const isDualRangePilot = calculationMethod === 'departure_range_pilot_updown'

    // For Avec pilote sections with EXTRA times
    const useExtraTimes = showExtraTimesOnly && extraTimeOptions.length > 0

    // Determine which options array to use
    let options
    let renderMode = 'standard' // 'standard', 'dualRange', 'extraDescOnly', 'extraDual'

    if (useExtraTimes) {
        options = [...extraTimeOptions]
        if (isDualRangePilot) {
            renderMode = 'extraDual' // Show A-B Ou/Or C-D
        } else {
            // Scenario 3: explicit (departure_range_pilot_desc) or legacy (departure_range + shoal)
            renderMode = 'extraDescOnly' // Show C-D only
        }
    } else if (isDualRangePilot && dualRangeOptions.length > 0) {
        options = [...dualRangeOptions]
        renderMode = 'dualRange'
    } else {
        options = [...departureOptions]
        renderMode = 'standard'
    }

    // Ensure we have exactly 4 options (for consistent numbering)
    while (options.length < 4) {
        if (
            renderMode === 'dualRange' ||
            renderMode === 'extraDual' ||
            renderMode === 'extraDescOnly'
        ) {
            options.push({ rising: null, descending: null })
        } else {
            options.push({ start: null, end: null, windowTooShort: false })
        }
    }

    /**
     * Check if an option has data based on render mode
     */
    const hasOptionData = (option, index) => {
        if (renderMode === 'extraDual' || renderMode === 'dualRange') {
            return hasDualRangeData(option)
        }
        if (renderMode === 'extraDescOnly') {
            const desc = option?.descending
            // Require both start AND end to be valid dates
            return (
                desc &&
                desc.start &&
                moment(desc.start).isValid() &&
                desc.end &&
                moment(desc.end).isValid()
            )
        }
        // Standard mode
        return hasStandardData(option)
    }

    // Check if any options have data
    const hasAnyOptions = options.slice(0, 4).some((option, index) => hasOptionData(option, index))

    return (
        <>
            <h2>
                Options de départ<sup>2</sup> / Departure options<sup>2</sup> :
            </h2>
            {!hasAnyOptions ? (
                <p>
                    <i>Aucune option de départ disponible</i>
                    <br />
                    <i>No departure options available</i>
                </p>
            ) : (
                <table cellSpacing='10'>
                    <tbody>
                        {options.slice(0, 4).map((option, index) => {
                            const optionNum = index + 1

                            // Skip options without data (but keep numbering consistent)
                            if (!hasOptionData(option, index)) {
                                return null
                            }

                            // EXTRA times: Scenario 4 with dual ranges (A-B Ou/Or C-D)
                            if (renderMode === 'extraDual') {
                                return (
                                    <tr key={optionNum}>
                                        <td width={80} style={{ verticalAlign: 'top' }}>
                                            <b>Option {optionNum}</b>
                                        </td>
                                        <td>
                                            <DualTimeRange
                                                rising={option.rising}
                                                descending={option.descending}
                                            />
                                        </td>
                                    </tr>
                                )
                            }

                            // EXTRA times: Scenario 3 with descending only (C-D)
                            if (renderMode === 'extraDescOnly') {
                                const desc = option.descending
                                return (
                                    <tr key={optionNum}>
                                        <td width={80} style={{ verticalAlign: 'top' }}>
                                            <b>Option {optionNum}</b>
                                        </td>
                                        <td>
                                            <TimeRange
                                                start={desc.start}
                                                end={desc.end}
                                                adjustEnd={true}
                                            />
                                        </td>
                                    </tr>
                                )
                            }

                            // Dual range format (non-extra mode)
                            if (renderMode === 'dualRange') {
                                return (
                                    <tr key={optionNum}>
                                        <td width={80} style={{ verticalAlign: 'top' }}>
                                            <b>Option {optionNum}</b>
                                        </td>
                                        <td>
                                            <DualTimeRange
                                                rising={option.rising}
                                                descending={option.descending}
                                            />
                                        </td>
                                    </tr>
                                )
                            }

                            // Standard formats: Scenarios 1, 2, 3, 4 (sans pilote)
                            const hasValidStart = option.start && moment(option.start).isValid()
                            const hasValidEnd = option.end && moment(option.end).isValid()

                            // For Scenario 1 (latest_time), apply 1h adjustment and check if still valid
                            const adjustedEnd =
                                isLatestTime && hasValidEnd
                                    ? adjustEndTimeForPreparation(option.end)
                                    : option.end

                            // Check if window is too short (original duration < 1h)
                            // Per customer spec: check BEFORE the 1h subtraction
                            const windowTooShort =
                                hasValidStart &&
                                hasValidEnd &&
                                isWindowTooShort(option.start, option.end)

                            return (
                                <tr key={optionNum}>
                                    <td width={80} style={{ verticalAlign: 'top' }}>
                                        <b>Option {optionNum}</b>
                                    </td>
                                    <td>
                                        {isLatestTime ? (
                                            // Scenario 1: Single time (with 1h adjustment)
                                            // Show warning if original window < 1h
                                            windowTooShort ? (
                                                <>
                                                    <i>- {SHORT_WINDOW_MESSAGE_FR}</i>
                                                    <br />
                                                    <i>- {SHORT_WINDOW_MESSAGE_EN}</i>
                                                </>
                                            ) : (
                                                <b>
                                                    {moment(adjustedEnd).format(DATE_TIME_FORMAT)}
                                                </b>
                                            )
                                        ) : (
                                            // Scenarios 2, 3, 4: Time range with 1h adjustment on end
                                            <TimeRange
                                                start={option.start}
                                                end={option.end}
                                                adjustEnd={true}
                                            />
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            )}
        </>
    )
}
